import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/core/utils/cn';
import { useApp } from '@/lib/core/providers/appProvider';
import { ApiBlockListModel, MenuItemPropertiesModel } from '@/services/platform/api/model';
import { initializeTalkdeskChat, setTalkdeskCallbacks } from '@/lib/core/utils';
import { useTalkdeskIdentitySync } from '@/lib/core/hooks/useTalkdeskIdentitySync';
import { useAuthStore } from '@/lib/core/stores/useAuthStore';
import { useClientTranslation, useLocaleStore } from '@/lib/core';

// ── Types ─────────────────────────────────────────────────────────────────────

export type NavBarStyle = 'default' | 'light';

export interface AppNavBarProps {
  navStyle?: NavBarStyle;
}

interface NavItem {
  label: string;
  to: string;
  isConcierge: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function resolveNavItems(navigation?: ApiBlockListModel): {
  primary: NavItem[];
  expanded: NavItem[];
} {
  const primary: NavItem[] = [];
  const expanded: NavItem[] = [];

  if (!navigation?.items?.length) return { primary, expanded };

  const seen = new Set<string>();
  for (const block of navigation.items) {
    const props = block?.content?.properties as MenuItemPropertiesModel | undefined;
    if (!props) continue;

    const link = props.link?.[0];
    if (!link) continue;

    const to = link.route?.path ?? link.url ?? '/';
    const label = (link.title ?? '').toUpperCase();
    if (!label || seen.has(label)) continue;
    seen.add(label);

    const item: NavItem = { label, to, isConcierge: props?.isConcierge ?? false };

    if (props.showAlways) {
      primary.push(item);
    } else {
      expanded.push(item);
    }
  }

  return { primary, expanded };
}

function isLinkActive(to: string, pathname: string, locale: string): boolean {
  const path = to.split('?')[0].replace(/\/$/, '/') || '/';
  const lang = locale.split('-')[0];

  if (pathname === '/') {
    return pathname === path || path === `/${lang}/`;
  }

  return (
    pathname === path ||
    path === `${pathname}/` ||
    pathname.replace(new RegExp(`^/${lang}/`), `/`).startsWith(path + '/')
  );
}

// ── Style tokens ──────────────────────────────────────────────────────────────

/**
 * `light` style (dark text): translucent white with a frosted-glass backdrop
 * blur.
 */
function pillStyle(navStyle: NavBarStyle): React.CSSProperties {
  if (navStyle === 'light') {
    return { background: 'rgba(218, 218, 218, 0.5)', backdropFilter: 'blur(12px)' };
  }
  return {};
}

/**
 * `default` style (white text) renders its translucent grey + frosted-glass
 * blur via an overlay sitting behind the content.
 */
function pillOverlayStyle(navStyle: NavBarStyle): React.CSSProperties | null {
  if (navStyle === 'light') return null;
  return {
    background: 'rgba(146, 146, 146, 0.90)', //rgba(146, 146, 146, 0.45)
    mixBlendMode: 'exclusion',
    backdropFilter: 'blur(12px)',
  };
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function InfinityIcon({ color }: { color: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M10.18 9.32001C9.35999 8.19001 8.05001 7.45001 6.54001 7.45001C4.03001 7.45001 1.98999 9.49 1.98999 12C1.98999 14.51 4.03001 16.55 6.54001 16.55C8.23001 16.55 9.80001 15.66 10.67 14.21L12 12L13.32 9.78998C14.19 8.33998 15.76 7.45001 17.45 7.45001C19.96 7.45001 22 9.49 22 12C22 14.51 19.96 16.55 17.45 16.55C15.95 16.55 14.64 15.81 13.81 14.68"
        stroke={color}
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CloseIcon({ color }: { color: string }) {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <line x1="4" y1="4" x2="16" y2="16" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
      <line x1="16" y1="4" x2="4" y2="16" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

// ── AppNavBar ─────────────────────────────────────────────────────────────────

export function AppNavBar({ navStyle = 'default' }: AppNavBarProps) {
  const translation = useClientTranslation();
  const locale = useLocaleStore(state => state.locale);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isConciergeActive, setIsConciergeActive] = useState(false);
  const { user: authUser } = useAuthStore();

  useEffect(() => {
    setIsOpen(false);
    setIsConciergeActive(false);
  }, [pathname]);

  useEffect(() => {
    setTalkdeskCallbacks({
      onOpen: () => setIsConciergeActive(true),
      onClose: () => setIsConciergeActive(false),
    });
    return () => setTalkdeskCallbacks({});
  }, []);

  useTalkdeskIdentitySync();

  const { appSettings } = useApp();
  const { primary, expanded } = resolveNavItems(appSettings?.properties?.navigation);

  // const activePrimaryTo = primary.find(link => isLinkActive(link.to, pathname))?.to ?? null;
  // const hasActivePrimary = activePrimaryTo !== null;
  const hasActiveExpanded = expanded.some(link => isLinkActive(link.to, pathname, locale));

  const isLight = navStyle === 'light';
  const textColor = isLight ? 'text-[#1a1a1a]' : 'text-white';
  const iconColor = isLight ? '#1a1a1a' : 'white';
  const dotColor = isLight ? 'bg-[#1a1a1a]' : 'bg-white';
  const style = pillStyle(navStyle);
  const overlayStyle = pillOverlayStyle(navStyle);

  if (!primary.length && !expanded.length) return null;

  return (
    <nav
      className="pointer-events-none fixed right-0 left-0 z-40 flex justify-center"
      style={{ bottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }}
    >
      <div className="pointer-events-auto flex items-center gap-1.75">
        {/* ── Main pill ──────────────────────────────────────────────── */}
        <div className="relative flex h-12 items-center gap-4 rounded-[69px] px-6" style={style}>
          {overlayStyle && (
            <div aria-hidden="true" className="absolute inset-0 rounded-[69px]" style={overlayStyle} />
          )}
          {isOpen
            ? // Expanded state: show secondary links
              expanded.map((link, i) => {
                const active = isLinkActive(link.to, pathname, locale);
                return (
                  <button
                    key={`expanded-${link.label}-${i}`}
                    onClick={() => {
                      navigate(link.to);
                      setIsOpen(false);
                    }}
                    className={cn(
                      'relative flex h-3.5 cursor-pointer items-center justify-center overflow-visible transition-opacity',
                      textColor
                    )}
                  >
                    <span className="font-pp-neue-montreal text-[12px] leading-3.5 font-medium tracking-[0.5px] uppercase">
                      {link.label}
                    </span>
                    <span
                      aria-hidden="true"
                      className={cn(
                        'absolute -bottom-1.25 left-1/2 size-0.75 -translate-x-1/2 rounded-full transition-opacity',
                        dotColor,
                        active ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                  </button>
                );
              })
            : // Default state: show primary links
              primary.map((link, i) => {
                const active = isLinkActive(link.to, pathname, locale);
                const isConcierge = link?.isConcierge;

                if (isConcierge) {
                  return (
                    <button
                      key={`primary-${link.label}-${i}`}
                      onClick={() => initializeTalkdeskChat()}
                      className={cn(
                        'relative flex h-3.5 items-center justify-center overflow-visible transition-opacity',
                        textColor
                        // hasActivePrimary && !isConciergeActive ? 'opacity-50' : 'opacity-100'
                      )}
                    >
                      <span className="font-pp-neue-montreal text-[12px] leading-3.5 font-medium tracking-[0.5px] uppercase">
                        {authUser?.hasActiveMembership
                          ? link.label
                          : translation('App.Labels.CustomerService')}
                      </span>
                      <span
                        aria-hidden="true"
                        className={cn(
                          'absolute -bottom-1.25 left-1/2 size-0.75 -translate-x-1/2 rounded-full transition-opacity',
                          dotColor,
                          isConciergeActive ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                    </button>
                  );
                }

                return (
                  <Link
                    key={`primary-${link.label}-${i}`}
                    to={link.to}
                    className={cn(
                      'relative flex h-3.5 items-center justify-center overflow-visible transition-opacity',
                      textColor
                      // (hasActivePrimary || isConciergeActive) && !active ? 'opacity-50' : 'opacity-100'
                    )}
                  >
                    <span className="font-pp-neue-montreal text-[12px] leading-3.5 font-medium tracking-[0.5px] uppercase">
                      {link.label}
                    </span>
                    <span
                      aria-hidden="true"
                      className={cn(
                        'absolute -bottom-1.25 left-1/2 size-0.75 -translate-x-1/2 rounded-full transition-opacity',
                        dotColor,
                        active ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                  </Link>
                );
              })}
        </div>

        {/* ── Toggle button: ∞ ↔ ✕ ─────────────────────────────────── */}
        {expanded.length > 0 && (
          <button
            className="relative flex size-12 cursor-pointer items-center justify-center rounded-[69px]"
            style={style}
            aria-label={isOpen ? 'Close menu' : 'More options'}
            onClick={() => setIsOpen(!isOpen)}
          >
            {overlayStyle && (
              <div aria-hidden="true" className="absolute inset-0 rounded-[69px]" style={overlayStyle} />
            )}
            <div className="relative flex size-6 items-center justify-center">
              {isOpen ? <CloseIcon color={iconColor} /> : <InfinityIcon color={iconColor} />}
              <span
                aria-hidden="true"
                className={cn(
                  'absolute -bottom-px left-1/2 size-0.75 -translate-x-1/2 rounded-full transition-opacity',
                  dotColor,
                  !isOpen && hasActiveExpanded ? 'opacity-100' : 'opacity-0'
                )}
              />
            </div>
          </button>
        )}
      </div>
    </nav>
  );
}
